import type { FieldProp } from 'react-typed-form';

import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { color } from 'src/constants/color.css';

import { ErrorList, Stack, Text } from '../components';

type InputProps = Pick<
  React.ComponentProps<typeof html.input>,
  'type' | 'disabled' | 'placeholder' | 'autoFocus'
>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Props = InputProps &
  Readonly<{
    field: FieldProp<string | null>;
    required?: boolean;
    multiline?: boolean;
    label?: string | null;
    onChangeText?: (text: string) => void;
  }>;

export default function FieldText({
  field,
  required,
  multiline,
  label,
  type,
  disabled,
  onChangeText,
  ...rest
}: Props) {
  const isDisabled = disabled || field.isLoading;

  return (
    <Stack gap={0.5}>
      {label !== null && (
        <Text
          typeface="body.small.regular"
          color={isDisabled ? 'neutral60' : 'neutral80'}
        >
          {label ?? field.label}
        </Text>
      )}
      {multiline ? (
        <html.textarea
          style={[
            styles.input,
            styles.textarea,
            isDisabled && styles.inputDisabled,
          ]}
          value={field.value || ''}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const text = e.target.value;
            if (text.trim() === '') {
              field.handleValueChange(null);
              return;
            }

            if (onChangeText) {
              onChangeText(text);
              return;
            }

            field.handleValueChange(text);
          }}
          disabled={isDisabled}
          {...rest}
        />
      ) : (
        <html.input
          style={[styles.input, isDisabled && styles.inputDisabled]}
          value={field.value || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            field.handleValueChange(e.target.value)
          }
          disabled={isDisabled}
          {...rest}
        />
      )}
      <ErrorList errors={field.lastErrorList} />
    </Stack>
  );
}

const styles = css.create({
  input: {
    padding: 12,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    borderRadius: 8,
    fontSize: 16,
    fontFamily: 'Inter, sans-serif',
    lineHeight: 1.5,
    color: color.neutral100,
    backgroundColor: '#fff',
    outline: 'none',
    width: '100%',
    '::placeholder': {
      color: color.neutral40,
    },
  },
  textarea: {
    minHeight: 100,
    resize: 'vertical' as const,
  },
  inputDisabled: {
    backgroundColor: color.neutral15,
    color: color.neutral60,
    cursor: 'default',
  },
});
